// Content rendering utilities for Chat component
import React, { useState } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { chatStyles } from '../../styles/chat.styles';
import { ACCENT } from '../../theme/colors';
import { CollapsibleThinkBlock } from './CollapsibleThinkBlock';
import { renderMarkdownToHtml } from './markdownProcessing';

interface RenderCodeBlockProps {
  code: string;
  language: string;
  copiedCode?: string;
  onCopy: (code: string) => void;
}

export const renderCodeBlock = ({ code, language, copiedCode, onCopy }: RenderCodeBlockProps) => {
  return (
    <div style={chatStyles.codeBlock}>
      <div style={chatStyles.codeHeader}>
        <span>{language}</span>
        <div style={chatStyles.codeActions}>
          <button
            className="pa-icon-ghost"
            onClick={() => onCopy(code)}
            style={chatStyles.copyButton}
          >
            {copiedCode === code ? 'Copied' : 'Copy'}
          </button>
        </div>
      </div>
      <SyntaxHighlighter
        language={language.toLowerCase()}
        style={vscDarkPlus}
        customStyle={{
          margin: 0,
          borderRadius: '0 0 12px 12px',
          padding: '16px',
          whiteSpace: 'pre',
          fontFamily: 'monospace',
          backgroundColor: '#121418'
        }}
        wrapLines={false}
        showLineNumbers={false}
      >
        {code}
      </SyntaxHighlighter>
    </div>
  );
};

interface ProcessContentProps {
  content: string;
  theme: 'light' | 'dark';
  copiedCode?: string;
  onCopy: (code: string) => void;
  citationsMap?: { [key: string]: any };
}

export const processContent = ({ content, theme, copiedCode, onCopy, citationsMap }: ProcessContentProps): React.ReactNode => {
  // Split content by thinking blocks and code blocks to handle them separately
  const thinkingBlockRegex = /<think>([\s\S]*?)(?:<\/think>|$)/g;
  const codeBlockRegex = /(```[\s\S]*?```|```[\s\S]*$)/g;
  
  // First split by thinking blocks
  const thinkingSplit = content.split(thinkingBlockRegex);
  const elements: React.ReactNode[] = [];
  
  for (let i = 0; i < thinkingSplit.length; i++) {
    if (i % 2 === 0) {
      // This is regular content (not inside <think> tags)
      const regularContent = thinkingSplit[i];
      if (!regularContent) continue;
      
      // Now split this regular content by code blocks
      const codeSplit = regularContent.split(codeBlockRegex);
      
      for (let j = 0; j < codeSplit.length; j++) {
        const part = codeSplit[j];
        if (!part) continue;
        
        // Check for complete code blocks
        if (part.startsWith('```') && part.endsWith('```')) {
          const codeContent = part.replace(/^```/, '').replace(/```$/, '');
          const lines = codeContent.split('\n');
          const language = lines[0].trim();
          const code = lines.slice(1).join('\n');
          
          elements.push(
            <div key={`code-${i}-${j}`}>
              {renderCodeBlock({ code, language: language || 'text', copiedCode, onCopy })}
            </div>
          );
        }
        // Check for incomplete code blocks
        else if (part.startsWith('```') && !part.endsWith('```')) {
          const codeContent = part.replace(/^```/, '');
          const lines = codeContent.split('\n');
          const language = lines[0].trim();
          const code = lines.slice(1).join('\n');
          
          elements.push(
            <div key={`code-incomplete-${i}-${j}`} style={chatStyles.codeBlock}>
              <div style={chatStyles.codeHeader}>
                <span>{language || 'text'}</span>
                <div style={chatStyles.codeActions}>
                  <span style={{
                    fontSize: '12px',
                    color: '#888',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    <div style={{
                      width: '5px',
                      height: '5px',
                      backgroundColor: ACCENT,
                      borderRadius: '50%'
                    }}></div>
                    Typing...
                  </span>
                </div>
              </div>
              <SyntaxHighlighter
                language={language.toLowerCase() || 'text'}
                style={vscDarkPlus}
                customStyle={{
                  margin: 0,
                  borderRadius: '0 0 12px 12px',
                  padding: '16px',
                  whiteSpace: 'pre',
                  fontFamily: 'monospace',
                  backgroundColor: '#121418',
                  minHeight: '60px'
                }}
                wrapLines={false}
                showLineNumbers={false}
              >
                {code}
              </SyntaxHighlighter>
            </div>
          );
        }
        // Regular text content
        else {
          const processedText = processMarkdownText(part, theme, citationsMap);
          if (processedText) {
            elements.push(
              <span key={`text-${i}-${j}`} dangerouslySetInnerHTML={{ __html: processedText }} />
            );
          }
        }
      }
    } else {
      // This is content inside <think> tags
      const thinkContent = thinkingSplit[i];
      if (thinkContent) {
        elements.push(
          <CollapsibleThinkBlock 
            key={`think-${i}`} 
            content={thinkContent} 
            theme={theme} 
          />
        );
      }
    }
  }
  
  return <>{elements}</>;

};

// Helper function to process markdown text - delegates to the single source of
// truth in markdownProcessing.ts (shared with scripts/test-markdown.mjs).
const processMarkdownText = (text: string, theme: 'light' | 'dark', citationsMap?: { [key: string]: any }): string =>
  renderMarkdownToHtml(text, theme, citationsMap);

interface MessageContentProps {
  content: string;
  theme: 'light' | 'dark';
}

export const MessageContent: React.FC<MessageContentProps> = ({ content, theme }) => {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const renderCodeBlockSimple = (code: string, language: string) => {
    return (
      <div style={chatStyles.codeBlock}>
        <div style={chatStyles.codeHeader}>
          <span>{language}</span>
          <div style={chatStyles.codeActions}>
            <button 
              onClick={() => copyToClipboard(code)}
              style={chatStyles.copyButton}
            >
              {copied ? '✓ Copied' : 'Copy'}
            </button>
          </div>
        </div>
        <SyntaxHighlighter
          language={language}
          style={vscDarkPlus}
          customStyle={{ margin: 0, borderRadius: '0 0 6px 6px' }}
        >
          {code}
        </SyntaxHighlighter>
      </div>
    );
  };

  const parts = content.split(/(```[\s\S]*?```)/);
  
  return (
    <div>
      {parts.map((part, index) => {
        if (part.startsWith('```')) {
          const [language, ...codeLines] = part
            .replace(/```/g, '')
            .trim()
            .split('\n');
          const code = codeLines.join('\n');
          return renderCodeBlockSimple(code, language);
        }
        return <span key={index}>{part}</span>;
      })}
    </div>
  );
};

interface LazyTableProps {
  content: string;
  theme: 'light' | 'dark';
  currentThemeColors: any;
}

export const LazyTable: React.FC<LazyTableProps> = ({ content, theme, currentThemeColors }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  
  const handleCopy = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };
  
  const parts = content.split(/((?:\|.*\|\s*\n)+)/);
  const tableContent = parts.find(part => part.trim().startsWith('|'));
  
  if (!tableContent) {
    return <div>{processContent({ content, theme, copiedCode: copiedCode || undefined, onCopy: handleCopy })}</div>;
  }

  const lines = tableContent.split('\n');
  const headerIndex = lines.findIndex(line => line.includes('|'));
  if (headerIndex === -1) {
    return <div>{processContent({ content, theme, copiedCode: copiedCode || undefined, onCopy: handleCopy })}</div>;
  }

  const header = lines[headerIndex];
  const bodyRows = lines.slice(headerIndex + 2).filter(line => line.includes('|'));

  if (bodyRows.length <= 10) {
    return <div>{processContent({ content, theme, copiedCode: copiedCode || undefined, onCopy: handleCopy })}</div>;
  }

  return (
    <div>
      {parts[0] && <div>{processContent({ content: parts[0], theme, copiedCode: copiedCode || undefined, onCopy: handleCopy })}</div>}

      <div style={{
        overflowX: 'auto',
        margin: '12px 0',
        border: `1px solid ${theme === 'light' ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.06)'}`,
        borderRadius: '10px',
      }}>
        <table style={{
          borderCollapse: 'separate',
          borderSpacing: 0,
          width: '100%',
          fontSize: '13px',
          textAlign: 'left'
        }}>
          <thead>
            <tr style={{ backgroundColor: theme === 'light' ? '#F1F2F4' : 'rgba(255,255,255,0.03)' }}>
              {header.split('|')
                .slice(1, -1)
                .map((cell, i) => (
                  <th key={i} style={{
                    padding: '8px 12px',
                    textAlign: 'left',
                    fontWeight: 600,
                    fontSize: '10.5px',
                    letterSpacing: '0.07em',
                    textTransform: 'uppercase',
                    color: theme === 'light' ? '#8B9099' : '#5C6068',
                    whiteSpace: 'nowrap',
                    borderBottom: `1px solid ${theme === 'light' ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.06)'}`,
                  }}>
                    {cell.trim()}
                  </th>
                ))}
            </tr>
          </thead>
          <tbody>
            {(isExpanded ? bodyRows : bodyRows.slice(0, 10)).map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.split('|')
                  .slice(1, -1)
                  .map((cell, i) => (
                    <td key={i} style={{
                      padding: '8px 12px',
                      textAlign: 'left',
                      color: theme === 'light' ? '#3A3D42' : '#C9CCD2',
                      whiteSpace: 'nowrap',
                      borderBottom: `1px solid ${theme === 'light' ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.06)'}`,
                    }}>
                      {cell.trim()}
                    </td>
                  ))}
              </tr>
            ))}
          </tbody>
        </table>
        {!isExpanded && bodyRows.length > 10 && (
          <div style={{ 
            textAlign: 'center', 
            marginTop: '10px',
            paddingBottom: '10px'
          }}>
            <button
              onClick={() => setIsExpanded(true)}
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: currentThemeColors.hoverBackground,
                color: currentThemeColors.text,
                cursor: 'pointer'
              }}
            >
              Show {bodyRows.length - 10} more rows
            </button>
          </div>
        )}
      </div>

      {parts[2] && <div>{processContent({ content: parts[2], theme, copiedCode: copiedCode || undefined, onCopy: handleCopy })}</div>}
    </div>
  );
};

export const isLargeTable = (content: string): boolean => {
  const tableRows = content.match(/\|.*\|/g);
  return tableRows ? tableRows.length > 20 : false;
};